import React from 'react';
import {createPortal} from 'react-dom';

import {buttonSecondary} from '../lib/buttonStyles.js';
import Button from './Button.js';
import {
  MODAL_WIDTHS,
  dialogBodyStyle,
  dialogCloseStyle,
  dialogFooterStyle,
  dialogHeaderStyle,
  dialogMessageStyle,
  dialogPanelStyle,
  dialogScrimStyle,
  dialogTitleStyle,
  type ModalSize,
} from '../lib/dialogStyles.js';
import {useModalFocusTrap} from './useModalFocusTrap.js';

// ============================================================================
// Modal — the one modal shell (Design Constitution O01, O05, O06, Q26, Q38)
// ----------------------------------------------------------------------------
//   size:   sm 440 · md 640 · lg 920 (O01); a full-screen sheet on phones
//   title + × in the header; footer via <ModalFooter>: Delete far left (danger
//           text), then Cancel and the one green primary on the right
//   Esc, × and Cancel close; Esc closes only this, the top-most layer (O06)
//   backdrop closes only if nothing was typed (O01)
//   closing with typed data asks 'Discard Changes?' (Keep Editing / Discard,
//           O05); `dirty` lets a caller say so when typing is not the signal
//   autosave: a named autosave modal (C08, Q26) — one green 'Done', no Cancel,
//           no discard prompt (the typing is already saved)
//   preserveOnClose: the caller keeps its own draft (a form whose unfinished
//           answers are retained), so Esc, × and Cancel close without the
//           discard prompt. It changes the prompt ONLY: the footer keeps
//           Cancel and its own primary, and a backdrop click after typing
//           is still ignored (O01)
//   focus starts on the first field (else Cancel / the marked element), stays
//           inside, and returns to the opener (Q38)
// The modal renders into <body> through a portal so a lifted (transformed)
// ancestor can never become its containing block. It sits on the modals layer
// (--z-modal); motion and the phone sheet live in index.html.
// ============================================================================

export type CloseSource = 'esc' | 'close' | 'cancel' | 'backdrop';

// Whether a close request may proceed straight away, should ask 'Discard
// Changes?', or is ignored (a backdrop click after typing).
export function closeDecision(
  source: CloseSource,
  hasTyped: boolean,
  autosave: boolean,
  preserveOnClose = false,
): 'close' | 'ask' | 'ignore' {
  if (!hasTyped || autosave) return 'close';
  if (source === 'backdrop') return 'ignore';
  return preserveOnClose ? 'close' : 'ask';
}

export type ModalContextValue = {requestClose: (source: CloseSource) => void; autosave: boolean};
// Shared by Modal and Drawer, so <ModalFooter>'s Cancel and Done go through
// the same close request (Discard prompt) in either shell.
export const DialogContext = React.createContext<ModalContextValue | null>(null);

// First field, else the element the footer or caller marked (Cancel).
export const MODAL_INITIAL_FOCUS =
  'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [data-modal-initial-focus]';

type ModalProps = {
  title: React.ReactNode;
  onClose: () => void;
  size?: ModalSize;
  dirty?: boolean;
  autosave?: boolean;
  preserveOnClose?: boolean;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  // Data hooks a screen's tests already select by (data-*), kept when the
  // screen moves onto this shell.
  dialogAttrs?: Record<string, string>;
};

export function useDialogPortal(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.body;
}

export default function Modal({
  title,
  onClose,
  size = 'md',
  dirty = false,
  autosave = false,
  preserveOnClose = false,
  footer,
  children,
  dialogAttrs,
}: ModalProps) {
  const [typed, setTyped] = React.useState(false);
  const [asking, setAsking] = React.useState(false);
  const hasTyped = dirty || typed;
  const titleId = React.useId();

  const requestClose = React.useCallback(
    (source: CloseSource) => {
      const decision = closeDecision(source, hasTyped, autosave, preserveOnClose);
      if (decision === 'close') onClose();
      else if (decision === 'ask') setAsking(true);
    },
    [hasTyped, autosave, preserveOnClose, onClose],
  );
  const {dialogRef, handleDialogKeyDown} = useModalFocusTrap<HTMLDivElement>({
    onCancel: () => requestClose('esc'),
    initialFocusSelector: MODAL_INITIAL_FOCUS,
  });

  const layer = (
    <div
      data-dialog-scrim="1"
      onMouseDown={(e) => {
        // A click on the scrim must not move focus out of the dialog, or Esc
        // (handled on the dialog) would stop working (O06).
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        requestClose('backdrop');
      }}
      style={dialogScrimStyle}
    >
      <div
        {...dialogAttrs}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-dialog-panel={size}
        onInput={() => {
          if (!typed) setTyped(true);
        }}
        onKeyDown={(e) => {
          // Esc closes only the top-most layer (O06): stop it here so an outer
          // dialog's handler never sees it.
          if (e.key === 'Escape') e.stopPropagation();
          handleDialogKeyDown(e);
        }}
        style={dialogPanelStyle(MODAL_WIDTHS[size])}
      >
        <div style={dialogHeaderStyle}>
          <h2 id={titleId} style={dialogTitleStyle}>
            {title}
          </h2>
          <button
            type="button"
            className="wcf-clickable"
            aria-label="Close"
            data-dialog-close="1"
            onClick={() => requestClose('close')}
            style={dialogCloseStyle}
          >
            ×
          </button>
        </div>
        <div data-dialog-body="1" style={dialogBodyStyle}>
          {children}
        </div>
        <DialogContext.Provider value={{requestClose, autosave}}>{footer}</DialogContext.Provider>
      </div>
      {asking && (
        <DiscardPrompt
          onKeep={() => setAsking(false)}
          onDiscard={() => {
            setAsking(false);
            onClose();
          }}
        />
      )}
    </div>
  );
  const target = useDialogPortal();
  return target ? createPortal(layer, target) : layer;
}

// ── Footer (O01, Q26): Delete far left in danger text; Cancel, then the one
// green primary on the right. An autosave modal shows one green 'Done' and no
// Cancel. Cancel goes through the modal's close request (Discard prompt) unless
// the caller passes onCancel (a delete confirm closes straight away). Every
// button is the shared <Button>; busy swaps the primary's label, no spinner (F09).
type ModalFooterProps = {
  primaryLabel?: React.ReactNode;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  busy?: boolean;
  // The busy primary's label ('Saving…' by default; a delete confirm says 'Deleting…').
  busyLabel?: string;
  // A delete confirm step fills the primary in danger red instead of the green.
  primaryVariant?: 'primary' | 'destructive';
  onDelete?: () => void;
  deleteLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  // Cancel stays clickable unless set: a form whose save is in flight disables it.
  cancelDisabled?: boolean;
  onCancel?: () => void;
  // Data hooks a screen's tests select by (data-*), kept when its footer moves here.
  primaryAttrs?: Record<`data-${string}`, string>;
  cancelAttrs?: Record<`data-${string}`, string>;
  deleteAttrs?: Record<`data-${string}`, string>;
};

export function ModalFooter({
  primaryLabel = 'Save',
  onPrimary,
  primaryDisabled = false,
  busy = false,
  busyLabel,
  primaryVariant = 'primary',
  onDelete,
  deleteLabel = 'Delete',
  cancelLabel = 'Cancel',
  cancelDisabled = false,
  onCancel,
  primaryAttrs,
  cancelAttrs,
  deleteAttrs,
}: ModalFooterProps) {
  const ctx = React.useContext(DialogContext);
  const autosave = ctx?.autosave ?? false;
  const cancel = () => {
    if (onCancel) onCancel();
    else ctx?.requestClose('cancel');
  };
  return (
    <div data-dialog-footer="1" style={dialogFooterStyle}>
      {onDelete && (
        <Button {...deleteAttrs} variant="danger" data-dialog-delete="1" onClick={onDelete}>
          {deleteLabel}
        </Button>
      )}
      <div style={{marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end'}}>
        {!autosave && (
          <Button
            {...cancelAttrs}
            variant="secondary"
            data-modal-initial-focus="1"
            data-dialog-cancel="1"
            disabled={cancelDisabled}
            onClick={cancel}
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          {...primaryAttrs}
          variant={autosave ? 'primary' : primaryVariant}
          data-dialog-primary="1"
          disabled={primaryDisabled}
          busy={busy}
          busyLabel={busyLabel}
          onClick={autosave ? () => ctx?.requestClose('close') : onPrimary}
        >
          {autosave ? 'Done' : primaryLabel}
        </Button>
      </div>
    </div>
  );
}

// ── 'Discard Changes?' (O05): Keep Editing / Discard, on the confirms layer.
export function DiscardPrompt({onKeep, onDiscard}: {onKeep: () => void; onDiscard: () => void}) {
  const {dialogRef, handleDialogKeyDown} = useModalFocusTrap<HTMLDivElement>({onCancel: onKeep});
  return (
    <div
      data-discard-prompt="1"
      onMouseDown={(e) => {
        // A click on the scrim must not move focus out of the dialog, or Esc
        // (handled on the dialog) would stop working (O06).
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        onKeep();
      }}
      style={{...dialogScrimStyle, zIndex: 1}}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="discard-prompt-title"
        aria-describedby="discard-prompt-message"
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') e.stopPropagation();
          handleDialogKeyDown(e);
        }}
        style={dialogPanelStyle(MODAL_WIDTHS.sm)}
      >
        <div style={dialogHeaderStyle}>
          <h2 id="discard-prompt-title" style={dialogTitleStyle}>
            Discard Changes?
          </h2>
        </div>
        <div style={dialogBodyStyle}>
          <p id="discard-prompt-message" style={dialogMessageStyle}>
            What you typed here has not been saved.
          </p>
        </div>
        <div style={dialogFooterStyle}>
          <div style={{marginLeft: 'auto', display: 'flex', gap: 8}}>
            <button
              type="button"
              className="wcf-clickable wcf-btn wcf-btn--secondary"
              data-modal-initial-focus="1"
              data-discard-keep="1"
              onClick={onKeep}
              style={buttonSecondary}
            >
              Keep Editing
            </button>
            <button
              type="button"
              className="wcf-clickable wcf-btn wcf-btn--secondary"
              data-discard-confirm="1"
              onClick={onDiscard}
              style={{...buttonSecondary, color: 'var(--danger-ink)'}}
            >
              Discard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
